/**
 * The Southern Tool Steel body of `/metal-resources/`: the SEO head, a title hero, an anchor
 * sub-navigation (desktop only), and these sections in order: References (FAQ and glossary),
 * Tools & Calculators, Other References (the STS ISO 9001:2015 certificate and colour-code chart
 * PDFs), Your Local Metal Professionals, and Contact Us. The STS build is compiled but not
 * delivered; it waits for web-cms-consumer.
 *
 * Rendered by `src/pages/metal-resources/index.tsx` on any build other than Ryerson. The Ryerson
 * counterpart, with the same anchor rule and the same `hide` handling of Contact Us, is
 * `./MetalResourcesLandingRyerson.tsx`.
 *
 * The Other References PDFs are served from `NEXT_PUBLIC_URI_STATIC` under
 * `/static-assets/documents/pdfs/sts/<en|fr|es>/`, owned by web-static-assets.
 */
import React from "react";
import { AllSections, Sections } from "@components/Shared/AllSections/AllSections";
import { LanguageContent } from "@components/Shared/model/LanguageContent";
import { useApplication } from "@ryerson/frontend.application";
import ContactUsRefactor from "@components/Shared/ContactUsRefactor/ContactUs";
import { DefaultContactUsContent } from "@components/Shared/ContactUsRefactor/Content";
import YourMetalExperts, {
	YourMetalExpertsProps,
} from "@components/Shared/YourMetalExperts/YourMetalExperts";
import { YourStaticContent } from "@components/Shared/YourMetalExperts/Content";
import { Media } from "@ryerson/frontend.next";
import MetalResourcesHero, { MetalResourcesHeroProps } from "@components/MetalResources/Hero/Hero";
import ToolsCalculators, {
	ToolsCalculatorsProps,
} from "@components/MetalResources/ToolsCalculators/ToolsCalculators";
import References from "@components/MetalResources/References/References";
import { IgniteYourCareerContent } from "@components/Careers/IgniteYourCareer/IgniteYourCareer";
import SubNavigation, {
	NavigableItem,
} from "@components/BarTubeStructuralProcessing/SubNavigation/SubNavigation";
import Resources, {
	MetalResourcesContent,
} from "@components/References/MetalResources/MetalResources";
import MetaHead, { MetaContent } from "@components/Shared/model/MetaHead";

const MetaObject: LanguageContent<MetaContent> = {
	en: {
		title: "Metal Resources - Southern Tool Steel",
		description:
			"From frequently asked questions to our metal weight calculator, our metal resources can help answer your most pressing questions.",
		url: `/metal-resources`,
		company: true,
	},
	fr: {
		title: "Metal Resources - Southern Tool Steel",
		description:
			"From frequently asked questions to our metal weight calculator, our metal resources can help answer your most pressing questions.",
		url: `/metal-resources`,
		company: true,
	},
	es: {
		title: "Metal Resources - Southern Tool Steel",
		description:
			"From frequently asked questions to our metal weight calculator, our metal resources can help answer your most pressing questions.",
		url: `/metal-resources`,
		company: true,
	},
};

const ResourcesContent: LanguageContent<MetalResourcesContent> = {
	en: {
		title: "Other References",
		description: `At Southern Tool Steel, we understand how difficult it can be identifying drops and 
            left over pieces. That’s why we Color Code the full length of the bar, not just the ends.`,
		cards: [
			{
				imageUrl: "/images/metal-resources/sts/ISOUpdate.png",
				title: "ISO 9001:2015 Certification",
				actionUrl: `${process.env.NEXT_PUBLIC_URI_STATIC}/static-assets/documents/pdfs/sts/en/iso-9001-2015.pdf`,
			},
			{
				imageUrl: "/images/metal-resources/sts/ColorChartUpdate.png",
				title: "Color Code Chart",
				actionUrl: `${process.env.NEXT_PUBLIC_URI_STATIC}/static-assets/documents/pdfs/sts/en/color-code-update.pdf`,
			},
		],
	},
	fr: {
		title: "Autres références",
		description: `Chez Southern Tool Steel, nous comprenons combien il peut être difficile d’identifier les chutes et les restes de pièces. C’est pourquoi nous utilisons le code couleur sur toute la longueur de la barre, et pas seulement sur les extrémités.`,
		cards: [
			{
				imageUrl: "/images/metal-resources/sts/ISOUpdate.png",
				title: "Certification ISO 9001 2015",
				actionUrl: `${process.env.NEXT_PUBLIC_URI_STATIC}/static-assets/documents/pdfs/sts/fr/iso-9001-2015.pdf`,
			},
			{
				imageUrl: "/images/metal-resources/sts/ColorChartUpdate.png",
				title: "Tableau des codes de couleur STS",
				actionUrl: `${process.env.NEXT_PUBLIC_URI_STATIC}/static-assets/documents/pdfs/sts/fr/color-code-update.pdf`,
			},
		],
	},
	es: {
		title: "Otras referencias",
		description: `En Southern Tool Steel, comprendemos lo difícil que puede ser identificar las entregas y las piezas restantes. Por eso es que usamos el código de color para toda la longitud de la barra, no solo los extremos.`,
		cards: [
			{
				imageUrl: "/images/metal-resources/sts/ISOUpdate.png",
				title: "Certificación ISO 9001 2015",
				actionUrl: `${process.env.NEXT_PUBLIC_URI_STATIC}/static-assets/documents/pdfs/sts/es/iso-9001-2015.pdf`,
			},
			{
				imageUrl: "/images/metal-resources/sts/ColorChartUpdate.png",
				title: "Diagrama de códigos de color STS",
				actionUrl: `${process.env.NEXT_PUBLIC_URI_STATIC}/static-assets/documents/pdfs/sts/es/color-code-update.pdf`,
			},
		],
	},
};

const HeroContent: LanguageContent<MetalResourcesHeroProps> = {
	en: {
		title: "Metal Resources",
	},
	fr: {
		title: "Ressources métallurgiques",
	},
	es: {
		title: "Recursos de metal",
	},
};

const ReferencesContent: LanguageContent<IgniteYourCareerContent> = {
	en: {
		title1: "",
		title2: "References",
		description:
			"Get access to the resources and tools you need to address your most pressing questions.",
		cards: [
			{
				desktopImage: "/images/sts/metal-resources/ReferencesUpdate3.png",
				mobileImage: "/images/sts/metal-resources/ReferencesUpdate3Mobile.png",
				title: "FAQs",
				actionUrl: "/metal-resources/references/faq",
				actionLabel: "Find our answers",
				description:
					"Quick answers to need-to-know information related to our e-commerce site.",
			},
			{
				desktopImage: "/images/sts/metal-resources/STSReferenceMain.jpg",
				mobileImage: "/images/sts/metal-resources/STSReferenceMain.jpg",
				title: "Glossary",
				actionUrl: "/metal-resources/references/glossary",
				actionLabel: "Glossary",
				description:
					"What does that phrase mean? Find out with our comprehensive metal glossary.",
			},
		],
	},
	fr: {
		title1: "",
		title2: "Références",
		description:
			"Accédez aux ressources et aux outils dont vous avez besoin pour répondre à vos questions les plus urgentes.",
		cards: [
			{
				desktopImage: "/images/sts/metal-resources/ReferencesUpdate3.png",
				mobileImage: "/images/sts/metal-resources/ReferencesUpdate3Mobile.png",
				title: "Foire aux questions (FAQ)",
				actionUrl: "/metal-resources/references/faq",
				actionLabel: "Trouvez nos réponses",
				description:
					"Des réponses rapides à des questions essentielles liées à notre site de commerce électronique.",
			},
			{
				desktopImage: "/images/sts/metal-resources/STSReferenceMain.jpg",
				mobileImage: "/images/sts/metal-resources/STSReferenceMain.jpg",
				title: "Glossaire",
				actionUrl: "/metal-resources/references/glossary",
				actionLabel: "Glossaire",
				description:
					"Que signifie cette expression? Découvrez-le grâce à ce glossaire complet sur le métal.",
			},
		],
	},
	es: {
		title1: "",
		title2: "Referencias",
		description:
			"Obtenga acceso a los recursos y herramientas que necesita para resolver sus preguntas más urgentes.",
		cards: [
			{
				desktopImage: "/images/sts/metal-resources/ReferencesUpdate3.png",
				mobileImage: "/images/sts/metal-resources/ReferencesUpdate3Mobile.png",
				title: "Preguntas frecuentes",
				actionUrl: "/metal-resources/references/faq",
				actionLabel: "Encuentre nuestras respuestas",
				description:
					"Respuestas rápidas para información con base en la necesidad de conocimiento relacionado a nuestro centro de comercio electrónico.",
			},
			{
				desktopImage: "/images/sts/metal-resources/STSReferenceMain.jpg",
				mobileImage: "/images/sts/metal-resources/STSReferenceMain.jpg",
				title: "Glosario",
				actionUrl: "/metal-resources/references/glossary",
				actionLabel: "Glosario",
				description:
					"¿Qué significa esa frase? Descúbralo con este glosario integral de metal.",
			},
		],
	},
};

const ToolsCalculatorsContent: LanguageContent<ToolsCalculatorsProps> = {
	en: {
		title: "Tools & Calculators",
		mainDescription: `From quick conversions to calculating tools, access these resources to help you make the most informed decision for your next project.`,
		secondaryContent: [
			{
				title: "Weight \n Calculator",
				description: `Know the amount of metal you need for your next project.`,
				imageUrl: "/images/sts/metal-resources/STSReferenceCalculationsUpdate.jpg",
				link: "/metal-resources/tools-calculators/weight-calculator",
				linkText: "View Calculator",
			},
			{
				title: "Sheet Scrap \n Calculator",
				description: `We offer custom cut-to-size sheets in many products, gauges and finishes.
                    Curious as to how much money you can save with a custom size? Click here to learn more.`,
				imageUrl: "/images/sts/metal-resources/STSSheetScrapCalculatorUpdate.jpg",
				link: "/metal-resources/tools-calculators/sheet-scrap-calculator",
				linkText: "View Calculator",
			},
		],
	},
	fr: {
		title: "Outils et calculateurs",
		mainDescription: `Des conversions rapides aux outils de calcul, accédez à ces ressources pour vous aider à prendre la décision la plus éclairée pour votre prochain projet.`,
		secondaryContent: [
			{
				title: "Calculateur \n de poids",
				description: `Connaissez la quantité de métal dont vous avez besoin pour votre prochain projet.`,
				imageUrl: "/images/sts/metal-resources/STSReferenceCalculationsUpdate.jpg",
				link: "/metal-resources/tools-calculators/weight-calculator",
				linkText: "Afficher la calculatrice",
			},
			{
				title: "Calculateur \n de déchets",
				description: `Le calculateur peut vous aider à estimer le poids des pièces de métal en 
                diverses quantités.`,
				imageUrl: "/images/sts/metal-resources/STSSheetScrapCalculatorUpdate.jpg",
				link: "/metal-resources/tools-calculators/sheet-scrap-calculator",
				linkText: "Afficher la calculatrice",
			},
		],
	},
	es: {
		title: "Herramientas y calculadoras",
		mainDescription: `Desde conversiones rápidas hasta herramientas para calcular, acceda a estos recursos para ayudarle a tomar la decisión más informada para su próximo proyecto.`,
		secondaryContent: [
			{
				title: "Calculador de peso",
				description: `Conozca la cantidad de metal que necesita para su siguiente proyecto.`,
				imageUrl: "/images/sts/metal-resources/STSReferenceCalculationsUpdate.jpg",
				link: "/metal-resources/tools-calculators/weight-calculator",
				linkText: "Ver calculadora",
			},
			{
				title: "Calculador de \n Tamaños de Hoja",
				description: `El calculador puede ayudarle a estimar el peso de las partes metálicas en 
                diversas cantidades.`,
				imageUrl: "/images/sts/metal-resources/STSSheetScrapCalculatorUpdate.jpg",
				link: "/metal-resources/tools-calculators/sheet-scrap-calculator",
				linkText: "Ver calculadora",
			},
		],
	},
};

/** Renders the STS Metal Resources landing in the visitor's language. */
const MetalResourcesLandingSTS: React.FC<YourMetalExpertsProps> = ({ locationsWithExperts }) => {
	const {
		localization: { language },
	} = useApplication();

	const sections: Sections[] = [];
	const subNav: NavigableItem[] = [];

	sections.push({
		title: ReferencesContent[language].title2,
		cmp: <References content={ReferencesContent} />,
	});
	subNav.push({
		id: ReferencesContent[language].title2.toLowerCase().replace(/\s+/g, "-"),
		title: ReferencesContent[language].title2,
	});

	sections.push({
		title: ToolsCalculatorsContent[language].title,
		cmp: <ToolsCalculators {...ToolsCalculatorsContent[language]} />,
	});
	subNav.push({
		id: ToolsCalculatorsContent[language].title.toLowerCase().replace(/\s+/g, "-"),
		title: ToolsCalculatorsContent[language].title,
	});

	subNav.push({
		id: ResourcesContent[language].title.toLowerCase().replace(/\s+/g, "-"),
		title: ResourcesContent[language].title,
	});
	sections.push({
		title: ResourcesContent[language].title,
		cmp: <Resources content={ResourcesContent[language]} />,
	});
	sections.push({
		title: YourStaticContent[language].navLabel,
		cmp: <YourMetalExperts locationsWithExperts={locationsWithExperts} />,
	});
	sections.push({
		title: DefaultContactUsContent[language].title,
		cmp: <ContactUsRefactor oneButtonVariant={false} />,
		hide: true,
	});

	return (
		<>
			<MetaHead content={MetaObject[language]} />
			<MetalResourcesHero {...HeroContent[language]} />
			<Media greaterThanOrEqual="lg">
				<SubNavigation navigableItems={subNav} type="primary" />
			</Media>
			<AllSections sections={sections} />
			<Media lessThan="lg">
				<ContactUsRefactor oneButtonVariant={false} background="secondary" />
			</Media>
		</>
	);
};

export default MetalResourcesLandingSTS;
